import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';

const formatWon = (value) => `${Math.round(Number(value) || 0).toLocaleString('ko-KR')}원`;

const Row = ({ label, value, bold }) => (
  <TableRow>
    <TableCell sx={{ width: '35%', color: 'text.secondary' }}>{label}</TableCell>
    <TableCell sx={{ fontWeight: bold ? 'bold' : 'normal' }}>{value}</TableCell>
  </TableRow>
);

/** 4단계: 견적서 미리보기(부품별 표 + 합계) + 엑셀 다운로드 + 견적 저장 + 처음부터. */
const CadQuotePreviewStep = ({
  form, costInputs, computed, onDownloadExcel, onRestart,
  savedQtUuid, saveQuoteLoading, onSaveQuote,
  finalAmtInput, onFinalAmtInputChange, finalAmtSaving, onSaveFinalAmt,
}) => {
  const partsById = Object.fromEntries((form.parts || []).map((p) => [p.id, p]));

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>4. 견적서 미리보기</Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        {form.partNo || '(품번 미지정)'} - {
          // eslint-disable-next-line no-nested-ternary
          form.isAssembly
            ? `조립품(부품 ${form.parts.length}종, 총 ${form.instanceCount}개)`
            : form.layered ? `레이어로 나눔(${form.parts.length}개)` : '단일 부품'
        }
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>품번/이름</TableCell>
              <TableCell>재질</TableCell>
              <TableCell align="right">수량</TableCell>
              <TableCell align="right">소재비</TableCell>
              <TableCell align="right">가공비</TableCell>
              <TableCell align="right">구매비</TableCell>
              <TableCell align="right">소계</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {computed.lines.map((line) => {
              const part = partsById[line.partId];
              if (!part) return null;
              return (
                <TableRow key={line.partId}>
                  <TableCell>{part.name}</TableCell>
                  <TableCell>{part.purchased ? '-' : part.material}</TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell align="right">{formatWon(line.materialCost)}</TableCell>
                  <TableCell align="right">{formatWon(line.machiningCost)}</TableCell>
                  <TableCell align="right">{formatWon(line.purchaseCost)}</TableCell>
                  <TableCell align="right">{formatWon(line.subtotal)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          <Row label="소재비 합계" value={formatWon(computed.materialCost)} />
          <Row label="가공비 합계" value={formatWon(computed.machiningCost)} />
          <Row label="구매비 합계" value={formatWon(computed.purchaseCost)} />
          <Row label="조립비" value={formatWon(computed.assemblyLaborCost)} />
          <Row label="외주" value={formatWon(computed.outsourceTotal)} />
          <Row label="마진" value={`${Math.round(costInputs.marginRate * 100)}%`} />
          <Row label="공급가" value={formatWon(computed.supplyPrice)} bold />
          <Row label="부가세" value={formatWon(computed.vat)} />
          <Row label="합계" value={formatWon(computed.total)} bold />
        </TableBody>
      </Table>

      <Divider sx={{ mb: 1 }} />
      <Typography variant="subtitle2">산출 근거</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
        {computed.lines.map((line) => {
          const part = partsById[line.partId];
          if (!part) return null;
          return (
            <Typography component="li" variant="body2" color="text.secondary" key={line.partId}>
              {part.name}: {part.purchased
                ? `구매품 단가 ${(part.purchasedUnitPrice || 0).toLocaleString('ko-KR')}원 × 수량 ${part.quantity}`
                : `소재 블록 ${part.stockSize.x}×${part.stockSize.y}×${part.stockSize.z}mm(여유 ${part.stockMarginMm}mm×2) = `
                  + `${line.stockVolumeCm3.toFixed(2)}cm³, 밀도 ${part.density}g/cm³ × kg단가 ${part.pricePerKg.toLocaleString('ko-KR')}원, `
                  + `제거 ${line.removalVolumeCm3.toFixed(2)}cm³÷황삭${part.roughRate}cm³/분=${line.roughMinutes.toFixed(1)}분, `
                  + `표면적÷정삭${part.finishRate}cm²/분=${line.finishMinutes.toFixed(1)}분, 셋업 ${part.setupMinutes}분`}
            </Typography>
          );
        })}
        <Typography component="li" variant="body2" color="text.secondary">
          시간당 가공비 {costInputs.hourlyRateWon.toLocaleString('ko-KR')}원
          {costInputs.assemblyMinutes ? `, 조립비 ${costInputs.assemblyMinutes}분` : ''}
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        시범 기능 — 영업기회 연결은 본개발 때 추가됩니다. 단가표 저장·비슷한 과거 견적·견적
        저장은 이제 됩니다.
      </Alert>

      {savedQtUuid ? (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'success.50' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            견적이 저장됐습니다(UUID: {savedQtUuid}). 나중에 고객에게 실제로 견적한 금액을
            입력해두면 다음 비슷한 견적에서 "실제 견적가"로 참고할 수 있습니다.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="number"
              size="small"
              label="실제 견적가(원)"
              value={finalAmtInput}
              onChange={(e) => onFinalAmtInputChange(e.target.value)}
              sx={{ width: 180 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={finalAmtSaving}
              onClick={onSaveFinalAmt}
            >
              {finalAmtSaving ? '저장 중...' : '실제 견적가 입력'}
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onRestart}>처음부터</Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onDownloadExcel}>엑셀로 받기</Button>
        <Button
          variant="contained"
          startIcon={saveQuoteLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSaveQuote}
          disabled={saveQuoteLoading || Boolean(savedQtUuid)}
        >
          {savedQtUuid ? '저장됨' : '견적 저장'}
        </Button>
      </Stack>
    </Paper>
  );
};

export default CadQuotePreviewStep;
